/*
  Search popup (sections/search-modal.liquid).

  - Every header search trigger opens it. The listeners run in the capture
    phase on document and stop the event there, so the theme's own handlers
    (Impulse's header search drawer, cst_mobile_predictive.js, the desktop
    predictive field) never see the click.
  - Before typing: the suggested searches from the section blocks. They are
    plain links to the search page for that text.
    After typing: /search/suggest rendered through
    sections/search-modal-results.liquid, grouped by type.
  - Opened with showModal(), so it sits in the top layer. The input is
    focused inside the same tap, which is what makes iOS raise the keyboard.
  - A history entry is pushed on open so the phone back gesture closes it.
*/
(function () {
  if (customElements.get('search-modal')) return;

  var TRIGGERS = [
    '#cst_mobile_search',
    '.js-search-header',
    '.js-cfw-desk-search',
    // The magnifier at the left of the desktop header field is a submit button.
    'predictive-search[data-context="header"] .btn--search_cstm',
    '[data-search-modal-open]'
  ].join(',');
  // The desktop header search field (Impulse predictive search, header context).
  var HEADER_FIELD = 'predictive-search[data-context="header"] input[type="search"]';

  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('.sm__input');
      this.results = this.querySelector('[data-sm-results]');
      this.suggest = this.querySelector('[data-sm-suggest]');
      this.clearBtn = this.querySelector('[data-sm-clear]');
      this.url = this.dataset.url;
      this.limit = this.dataset.limit || 4;
      this.cache = new Map();
      this.fallback = this.querySelector('[data-sm-fallback]');
      // Set after the first 417: predictive search doesn't serve this locale.
      this.unsupported = false;
      this.closedAt = 0;
      this.ownsHistory = false;

      this.onTrigger = this.onTrigger.bind(this);
      document.addEventListener('click', this.onTrigger, true);
      document.addEventListener('focusin', this.onTrigger, true);

      this.input.addEventListener('input', this.debounce(this.onInput.bind(this), 220));
      this.input.addEventListener('input', this.syncClear.bind(this));

      this.querySelector('[data-sm-close]').addEventListener('click', () => this.close());
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.syncClear();
        this.showSuggestions();
        this.input.focus();
      });

      // Click on the dark area around the panel closes.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
      // Escape: let our close() run so history stays in step.
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => this.afterClose());

      this.results.addEventListener('click', (e) => {
        var tab = e.target.closest('[data-sm-tab]');
        if (!tab) return;
        this.selectTab(tab.dataset.smTab);
      });

      window.addEventListener('popstate', () => {
        if (this.dialog.open) {
          this.ownsHistory = false;
          this.close();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger, true);
      document.removeEventListener('focusin', this.onTrigger, true);
    }

    onTrigger(e) {
      if (this.dialog.open) return;
      var t = e.target instanceof Element ? e.target : null;
      if (!t) return;

      var trigger = t.closest(TRIGGERS);
      if (trigger && e.type === 'click') {
        e.preventDefault();
        e.stopPropagation();
        this.open();
        return;
      }
      if (trigger) return;

      var field = t.matches(HEADER_FIELD) ? t : null;
      if (!field) return;
      // Focus comes back to the header field when the dialog closes; don't
      // reopen on that.
      if (e.type === 'focusin' && Date.now() - this.closedAt < 600) return;
      e.preventDefault();
      e.stopPropagation();
      var typed = field.value;
      field.blur();
      this.open(typed);
    }

    open(value) {
      if (typeof value === 'string' && value.trim()) {
        this.input.value = value;
        this.search(value.trim());
      } else if (!this.input.value.trim()) {
        this.showSuggestions();
      }
      this.syncClear();
      document.documentElement.classList.add('sm-lock');
      this.dialog.showModal();
      this.followViewport(true);
      this.input.focus();
      try {
        history.pushState({ searchModal: true }, '');
        this.ownsHistory = true;
      } catch (err) {}
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.close();
      if (this.ownsHistory) {
        this.ownsHistory = false;
        history.back();
      }
    }

    afterClose() {
      this.closedAt = Date.now();
      this.followViewport(false);
      document.documentElement.classList.remove('sm-lock');
      this.input.setAttribute('aria-expanded', 'false');
    }

    // iOS Safari: once the page has been scrolled (toolbar collapsed), the
    // keyboard pans the visual viewport down inside the layout viewport, and a
    // fixed full-screen dialog moves up with it, so the field ends up above the
    // screen. Pin the dialog to the visual viewport instead: its top at the
    // pan offset, its height down to the keyboard.
    followViewport(on) {
      var vv = window.visualViewport;
      if (!vv) return;
      if (!this.onViewport) {
        this.onViewport = () => {
          if (this.vvFrame) return;
          this.vvFrame = requestAnimationFrame(() => {
            this.vvFrame = 0;
            this.dialog.style.setProperty('--sm-vv-top', vv.offsetTop + 'px');
            this.dialog.style.setProperty('--sm-vv-h', vv.height + 'px');
          });
        };
      }
      if (on) {
        vv.addEventListener('resize', this.onViewport);
        vv.addEventListener('scroll', this.onViewport);
        this.onViewport();
      } else {
        vv.removeEventListener('resize', this.onViewport);
        vv.removeEventListener('scroll', this.onViewport);
        this.dialog.style.removeProperty('--sm-vv-top');
        this.dialog.style.removeProperty('--sm-vv-h');
      }
    }

    syncClear() {
      this.clearBtn.hidden = !this.input.value.length;
    }

    showSuggestions() {
      if (this.controller) this.controller.abort();
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.suggest) this.suggest.hidden = false;
      this.input.setAttribute('aria-expanded', 'false');
    }

    onInput() {
      var q = this.input.value.trim();
      if (!q) {
        this.showSuggestions();
        return;
      }
      this.search(q);
    }

    search(q) {
      if (this.unsupported) {
        this.renderFallback(q);
        return;
      }
      if (this.cache.has(q)) {
        this.render(this.cache.get(q));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      var params = new URLSearchParams({
        q: q,
        'resources[type]': 'product,article,page,collection',
        'resources[limit]': this.limit,
        'resources[limit_scope]': 'each',
        'resources[options][prefix]': 'last',
        section_id: 'search-modal-results'
      });
      this.classList.add('is-loading');
      fetch(this.url + '?' + params.toString(), { signal: this.controller.signal })
        .then((r) => {
          if (r.status === 417) {
            this.unsupported = true;
            this.renderFallback(q);
            return null;
          }
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then((text) => {
          if (text === null) return;
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var el = doc.querySelector('.sm-res');
          var html = el ? el.outerHTML : '';
          this.cache.set(q, html);
          // A slower earlier request must not overwrite a newer query.
          if (this.input.value.trim() === q) this.render(html);
        })
        .catch(() => {})
        .finally(() => this.classList.remove('is-loading'));
    }

    renderFallback(q) {
      if (!this.fallback) return;
      var node = this.fallback.content.firstElementChild.cloneNode(true);
      var link = node.querySelector('a');
      link.href += encodeURIComponent(q);
      link.textContent = link.textContent.replace('__SM_TERMS__', q);
      this.results.innerHTML = '';
      this.results.appendChild(node);
      this.results.hidden = false;
      if (this.suggest) this.suggest.hidden = true;
    }

    render(html) {
      if (!this.input.value.trim()) return;
      this.results.innerHTML = html;
      this.results.hidden = false;
      if (this.suggest) this.suggest.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
    }

    selectTab(type) {
      this.results.querySelectorAll('[data-sm-tab]').forEach((b) => {
        b.setAttribute('aria-selected', String(b.dataset.smTab === type));
      });
      this.results.querySelectorAll('[data-sm-panel]').forEach((p) => {
        p.classList.toggle('is-active', p.dataset.smPanel === type);
      });
      var res = this.results.querySelector('.sm-res');
      if (res) res.dataset.smActive = type;
    }

    debounce(fn, wait) {
      var t;
      return function () {
        var args = arguments;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(null, args); }, wait);
      };
    }
  }

  customElements.define('search-modal', SearchModal);
})();
